import React, { useMemo } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
    apiGetContract,
} from 'src/features/payments/api/payments.api';
import IntegratedPaymentMilestoneTable from '../components/IntegratedPaymentMilestoneTable';
import ActualPaymentModal from 'src/features/payments/components/modals/ActualPaymentModal';
import ChangeOrderPage from 'src/features/contracts/components/changeOrders/ChangeOrderPage';
import { useProjectAccessCtx } from 'src/features/projects/ProjectAccessContext';
import { hasPerm } from 'src/features/projects/hooks/useProjectAccess';

export default function ContractDetailPageV2() {
    const { projectId, contractId } = useParams();
    const [sp, setSp] = useSearchParams();
    const navigate = useNavigate();
    const activeTab = sp.get('tab') || 'milestones';

    const [paymentModal, setPaymentModal] = React.useState<{ 
        open: boolean; 
        milestoneId: string;
        amountPlan: number;
        currentPaid: number;
    }>({
        open: false,
        milestoneId: '',
        amountPlan: 0,
        currentPaid: 0,
    });

    // (1) Detailed Contract Query (includes milestones & payments)
    const { data: contract, isLoading: loadingContract, refetch: refetchContract } = useQuery({
        queryKey: ['contractDetail', projectId, contractId],
        queryFn: () => apiGetContract(projectId ?? '', contractId ?? ''),
        enabled: !!projectId && !!contractId,
    });

    const milestones = contract?.milestones ?? [];

    // Permissions
    const { access, isLoading: accessLoading } = useProjectAccessCtx();
    const canEditContract = useMemo(() => !accessLoading && hasPerm(access?.permissions, 'contract.edit'), [accessLoading, access]);

    if (!projectId || !contractId) return null;

    const handleAddPayment = (milestoneId: string, amountPlan: number, currentPaid: number) => {
        setPaymentModal({ open: true, milestoneId, amountPlan, currentPaid });
    };

    const formatCurrency = (val: number | null | undefined) => {
        if (val == null) return '—';
        return new Intl.NumberFormat('ko-KR', { maximumFractionDigits: 0 }).format(val);
    };

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="bg-white rounded-2xl border p-6 shadow-sm">
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                    <div className="flex items-center gap-4">
                        <button 
                            onClick={() => navigate(`/projects/${projectId}/contracts`)}
                            className="p-2 hover:bg-gray-100 rounded-full transition-colors"
                        >
                            <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                            </svg>
                        </button>
                        <div>
                            <div className="flex items-center gap-2">
                                <h1 className="text-xl font-bold text-gray-900">{contract?.contractName || 'Loading...'}</h1>
                                <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${contract?.direction === 'CLIENT' ? 'bg-emerald-100 text-emerald-700' : 'bg-blue-100 text-blue-700'}`}>
                                    {contract?.direction}
                                </span>
                            </div>
                            <p className="text-sm text-gray-500">Partner: {(contract as any)?.partyName || '—'}</p>
                        </div>
                    </div>
                    
                    <div className="flex items-center gap-6 px-6 py-3 bg-gray-50 rounded-xl border border-gray-100">
                        <div>
                            <p className="text-[10px] text-gray-400 uppercase font-bold">Total Amount</p>
                            <p className="text-sm font-bold text-gray-900">{formatCurrency((contract as any)?.contractAmount)}</p>
                        </div>
                        <div className="h-8 w-px bg-gray-200"></div>
                        <div>
                            <p className="text-[10px] text-gray-400 uppercase font-bold">Period</p>
                            <p className="text-sm font-medium text-gray-700">
                                {contract?.startDate || '—'} ~ {contract?.endDate || '—'}
                            </p>
                        </div>
                    </div>
                </div>
            </div>

            {/* Tabs */}
            <div className="border-b border-gray-200">
                <nav className="-mb-px flex space-x-8">
                    <button
                        onClick={() => setSp({ tab: 'milestones' })}
                        className={`py-4 px-1 border-b-2 font-medium text-sm transition-all ${activeTab === 'milestones' ? 'border-gray-900 text-gray-900' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`}
                    >
                        Milestones & Payments
                    </button>
                    <button
                        onClick={() => setSp({ tab: 'co' })}
                        className={`py-4 px-1 border-b-2 font-medium text-sm transition-all ${activeTab === 'co' ? 'border-gray-900 text-gray-900' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`}
                    >
                        Change Orders
                    </button>
                    <button
                        onClick={() => setSp({ tab: 'overview' })}
                        className={`py-4 px-1 border-b-2 font-medium text-sm transition-all ${activeTab === 'overview' ? 'border-gray-900 text-gray-900' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`}
                    >
                        Overview
                    </button>
                </nav>
            </div>

            {/* Content */}
            <div>
                {activeTab === 'milestones' && (
                    <div className="space-y-4">
                        {loadingContract ? (
                            <div className="flex justify-center py-12">
                                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
                            </div>
                        ) : contract ? (
                            <IntegratedPaymentMilestoneTable 
                                contract={contract}
                                milestones={milestones}
                                canEdit={canEditContract}
                                onAddPayment={handleAddPayment}
                            />
                        ) : null}
                    </div>
                )}

                {activeTab === 'co' && (
                    <ChangeOrderPage 
                        projectId={projectId}
                        contractId={contractId}
                    />
                )}

                {activeTab === 'overview' && (
                    <div className="bg-white rounded-2xl border p-8 text-center">
                        <p className="text-sm text-gray-500 italic">Overview metrics coming soon...</p>
                    </div>
                )}
            </div>

            <ActualPaymentModal
                open={paymentModal.open}
                onClose={() => {
                    setPaymentModal({ open: false, milestoneId: '', amountPlan: 0, currentPaid: 0 });
                    refetchContract();
                }}
                projectId={projectId}
                contractId={contractId}
                milestoneId={paymentModal.milestoneId}
                amountPlan={paymentModal.amountPlan}
                currentPaid={paymentModal.currentPaid}
            />
        </div>
    );
}
